/* MASONTEX · React Media Island (MVP)
   Replaces only the product gallery interaction inside
   sections/ms-v12-product-overview.liquid.
   Liquid SSR + Media Core (window.MasontexMediaCore) stay owners of data,
   resolution, preload and cache. React owns interaction state + rendering.
   Contracts preserved: oem-lite:ready / oem-lite:change, dataset flags,
   identical CSS classes (no CSS rewrite). */
(function () {
  'use strict';

  const React = window.React;
  const ReactDOM = window.ReactDOM;
  if (!React || !ReactDOM) {
    console.error('[MASONTEX Media React] missing React or ReactDOM');
    return;
  }

  const { useState, useEffect, useMemo, useRef } = React;
  const h = React.createElement;
  const getMountRegistry = () => {
    if (!window.__MASONTEX_MEDIA_REACT_MOUNTS) {
      window.__MASONTEX_MEDIA_REACT_MOUNTS = new WeakSet();
    }
    return window.__MASONTEX_MEDIA_REACT_MOUNTS;
  };
  const coreReady = () => {
    const core = window.MasontexMediaCore;
    return !!(core && core.normalize && core.capabilities && core.resolver && core.preload && core.cache && core.delivery);
  };

  const readJson = (root, selector, fallback) => {
    const scope = root.closest('section') || root;
    const node = scope.querySelector(selector);
    return window.MasontexMediaCore.normalize.parseJson(node && node.textContent, fallback);
  };

  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { failed: false };
    }
    static getDerivedStateFromError() {
      return { failed: true };
    }
    componentDidCatch(error) {
      try {
        const fallback = this.props.root.querySelector('[data-ssr-hero]');
        if (fallback) fallback.removeAttribute('hidden');
        this.props.root.dataset.mediaUnavailable = 'true';
      } catch (e) { /* ignore */ }
      console.error('[MASONTEX Media React] island error', error);
    }
    render() {
      return this.state.failed ? null : this.props.children;
    }
  }

  function MediaIsland({ root }) {
    const { normalize, capabilities, resolver, preload, cache, delivery } = window.MasontexMediaCore;
    const scope = useMemo(() => root.closest('section') || root, [root]);
    const config = useMemo(() => readJson(root, '[data-lite-config]', {}), [root, scope]);
    const productMedia = useMemo(
      () => readJson(root, '[data-lite-media]', []).filter((item) => item && item.image),
      [root, scope]
    );
    const native = useMemo(() => readJson(root, '[data-ms-product-page-i18n]', {}), [root, scope]);
    const displayValues = native.values || {};
    const overviewText = native.overview || {};

    const hasFullConfig = normalize.hasFullConfig(config);
    const lists = useMemo(
      () => normalize.buildLists({ config, productMedia, dataset: root.dataset, hasFullConfig }),
      [config, productMedia, hasFullConfig, root]
    );
    const defaults = config.defaultState || {};
    const initial = useMemo(() => normalize.initialState(lists, defaults), [lists, defaults]);

    const [color, setColor] = useState(initial.color);
    const [visor, setVisor] = useState(initial.visor);
    const [logo, setLogo] = useState(initial.logo);
    const [structure, setStructure] = useState(initial.structure);
    const [view, setView] = useState(initial.view);
    const [graphics, setGraphics] = useState(initial.graphics || '');
    const [custom, setCustom] = useState([]);
    const [notes, setNotes] = useState('');
    const [activeTab, setActiveTab] = useState(
      root.dataset.showColor !== 'false' && lists.colors.length ? 'colors' : 'visors'
    );
    const [loading, setLoading] = useState(true);
    const [overlayReady, setOverlayReady] = useState(false);

    const labels = {
      color: root.dataset.labelColor || 'COLOR',
      visor: root.dataset.labelVisor || 'VISOR',
      view: root.dataset.labelView || 'VIEW',
      structure: root.dataset.labelStructure || 'STRUCTURE',
      productView: root.dataset.labelProductView || 'PRODUCT VIEW',
      selected: root.dataset.labelSelected || 'SELECTED',
      reset: root.dataset.labelReset || 'RESET'
    };

    const state = { color, visor, logo, structure, view, graphics, custom, notes };
    const ctx = { config, productMedia, lists, hasFullConfig, state };

    const mediaCache = useRef(cache.createMediaCache(96));
    const warmCache = useRef(preload.createWarmCache());
    const mountedRef = useRef(false);
    const rootRef = useRef(root);

    const payloadFor = (c, v, s, w) => {
      const key = cache.payloadKey(c, v, s, w);
      const hit = mediaCache.current.get(key);
      if (hit !== undefined) return hit;
      const payload = resolver.payloadFor(ctx, c, v, s, w);
      if (payload) mediaCache.current.set(key, payload);
      return payload;
    };

    // ensureCompatible on mount + whenever color/visor changes (legacy parity)
    useEffect(() => {
      const next = resolver.ensureCompatible(ctx);
      if (next.structure !== structure) setStructure(next.structure);
      if (next.view !== view) setView(next.view);
      if (next.visor !== visor) setVisor(next.visor);
      mountedRef.current = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [color, visor]);

    const delivered = useMemo(() => {
      const candidates = resolver.imageCandidates(ctx, payloadFor);
      const options = { width: delivery.widthForViewport(window.innerWidth) };
      return candidates.map((payload) => delivery.transformPayload(payload, options));
    }, [state, lists, hasFullConfig]);

    const activePayload = delivered[0] || null;
    const activeBase = activePayload ? activePayload.base : '';

    // mediaUnavailable dataset contract
    useEffect(() => {
      const fallback = root.querySelector('[data-ssr-hero]');
      if (activePayload) {
        delete root.dataset.mediaUnavailable;
        if (fallback) fallback.setAttribute('hidden', '');
      } else {
        root.dataset.mediaUnavailable = '1';
        if (fallback) fallback.removeAttribute('hidden');
      }
    }, [activePayload, root]);

    // adaptive prewarm (debounced, same URL coverage)
    const warmForCurrentState = () => {
      const options = { width: delivery.widthForViewport(window.innerWidth) };
      const prefetchPayload = (c, v, s, w) => delivery.transformPayload(payloadFor(c, v, s, w), options);
      preload.prefetch(ctx, warmCache.current, prefetchPayload);
    };
    const warmScheduler = useRef(preload.createDebounced(warmForCurrentState, 500));

    const displayValue = (raw) => normalize.translate(raw, displayValues);
    const selectedLabel = (type) =>
      normalize.itemLabel(normalize.findItem(lists[`${type}s`] || [], state[type]), normalize.titleCase(state[type]));

    const configurationCode = () => [
      root.dataset.model || 'MODEL',
      normalize.findItem(lists.colors, color)?.code || normalize.shortCode(color, 'COL'),
      lists.visors.length ? (normalize.findItem(lists.visors, visor)?.code || normalize.shortCode(visor, 'VIS')) : 'NOV',
      normalize.findItem(lists.logos, logo)?.code || normalize.shortCode(logo, 'LOG'),
      root.dataset.moq || '200'
    ].join('-');

    const configuration = () => ({
      product: root.dataset.productTitle,
      productUrl: root.dataset.productUrl,
      model: root.dataset.model,
      color: selectedLabel('color'),
      visor: lists.visors.length ? selectedLabel('visor') : 'Not applicable',
      logo: lists.logos.length ? selectedLabel('logo') : 'Not applicable',
      structure: lists.structures.length ? selectedLabel('structure') : 'Not applicable',
      view: selectedLabel('view'),
      customOptions: custom,
      notes,
      moq: Number(root.dataset.moq || 200),
      certification: root.dataset.certification,
      code: configurationCode()
    });

    // summary / field sync into the SSR info column + change event
    useEffect(() => {
      const scope = root.closest('section') || root;
      const na = displayValues['N/A'] || 'N/A';
      const values = {
        model: root.dataset.model,
        color: displayValue(selectedLabel('color')),
        visor: lists.visors.length ? displayValue(selectedLabel('visor')) : na,
        logo: lists.logos.length ? displayValue(selectedLabel('logo')) : na,
        moq: root.dataset.moq
      };
      Object.entries(values).forEach(([name, value]) => {
        scope.querySelectorAll(`[data-lite-summary="${name}"]`).forEach((node) => { node.textContent = value; });
      });
      const current = scope.querySelector('[data-lite-current]');
      if (current) {
        current.textContent = [values.color, values.visor === na ? null : values.visor, values.logo === na ? null : values.logo]
          .filter(Boolean).join(' · ');
      }
      const stageLabel = scope.querySelector('[data-lite-stage-label]');
      if (stageLabel) {
        const selectedView = normalize.findItem(lists.views, view);
        const selectedStructure = normalize.findItem(lists.structures, structure);
        stageLabel.textContent = [
          normalize.viewLabelWithI18n(selectedView, Math.max(0, lists.views.indexOf(selectedView)), displayValues, overviewText),
          lists.structures.length ? displayValue(normalize.itemLabel(selectedStructure, normalize.titleCase(structure))) : null
        ].filter(Boolean).join(' · ').toUpperCase();
      }
      const configCode = scope.querySelector('[data-lite-code]');
      if (configCode) configCode.textContent = configurationCode();
      const quantity = scope.querySelector('[data-lite-quantity]');
      if (quantity) {
        const minimum = Number(root.dataset.moq || 200);
        quantity.min = String(minimum);
        if (Number(quantity.value) < minimum) quantity.value = String(minimum);
      }
      const details = configuration();
      scope.querySelectorAll('[data-lite-field]').forEach((input) => {
        const field = input.dataset.liteField;
        input.value = field === 'json'
          ? JSON.stringify(details)
          : Array.isArray(details[field])
            ? details[field].join(', ')
            : String(details[field] ?? '');
      });
      if (mountedRef.current) {
        root.dispatchEvent(new CustomEvent('oem-lite:change', { bubbles: true, detail: details }));
      }
    }, [color, visor, structure, view, logo, graphics, custom, notes]);

    // ready event on mount (after first summary sync)
    useEffect(() => {
      const fallback = root.querySelector('[data-ssr-hero]');
      if (fallback) fallback.setAttribute('hidden', '');
      root.dispatchEvent(new CustomEvent('oem-lite:ready', { bubbles: true, detail: configuration() }));
      root.dataset.oemLiteReady = '1';
      root.dataset.oemLiteVersion = '12.2-native-display';
      warmScheduler.current.schedule();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // adaptive prewarm after meaningful state change
    useEffect(() => {
      if (mountedRef.current) warmScheduler.current.schedule();
    }, [color, visor, structure, view]);

    // reset to initial state
    const reset = () => {
      setColor(initial.color);
      setVisor(initial.visor);
      setLogo(initial.logo);
      setStructure(initial.structure);
      setView(initial.view);
      setGraphics(initial.graphics || '');
      setCustom([]);
      setNotes('');
      const scope = root.closest('section') || root;
      scope.querySelectorAll('[data-lite-custom]').forEach((checkbox) => { checkbox.checked = false; });
      const notesEl = scope.querySelector('[data-lite-notes]');
      if (notesEl) notesEl.value = '';
    };

    const showColor = root.dataset.showColor !== 'false';
    const showVisor = root.dataset.showVisor !== 'false' && lists.visors.length > 0;
    const showViews = root.dataset.showViews !== 'false';
    const showStructure = root.dataset.showStructure !== 'false';
    const tabs = [];
    if (showColor) tabs.push({ key: 'colors', label: labels.color });
    if (showVisor) tabs.push({ key: 'visors', label: labels.visor });

    const tabNav = (event) => {
      const keys = tabs.map((t) => t.key);
      const index = keys.indexOf(activeTab);
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % keys.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + keys.length) % keys.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = keys.length - 1;
      if (next !== null) {
        event.preventDefault();
        setActiveTab(keys[next]);
      }
    };

    const choiceTone = (item, index) => item.tone
      || (Array.isArray(item.swatches) && item.swatches.length ? `linear-gradient(135deg,${item.swatches.join(',')})` : '')
      || item.swatch
      || item.hex
      || item.color
      || ['#f4f5f6', '#171b20', '#ffffff', '#7d8791'][index % 4];

    const renderChoiceList = (name, type) => {
      const items = lists[name] || [];
      const select = (key) => {
        if (type === 'color') setColor(key);
        else if (type === 'visor') setVisor(key);
        else if (type === 'logo') setLogo(key);
      };
      return h('div', { className: 'ms-lite-choice-grid' },
        items.map((item, index) => {
          const key = normalize.itemKey(item);
          const active = state[type] === key;
          const children = [];
          if (type !== 'logo') {
            children.push(h('i', { key: 'sw', style: { background: choiceTone(item, index) } }));
          }
          const label = displayValue(normalize.itemLabel(item, normalize.titleCase(key)));
          children.push(h('strong', { key: 'label' }, label));
          if (item.description) {
            const translated = displayValue(item.description);
            if (translated !== item.description || !/[A-Za-z]{3,}/.test(item.description)) {
              children.push(h('small', { key: 'desc' }, translated));
            }
          }
          return h('button', {
            key,
            type: 'button',
            className: 'ms-lite-choice' + (active ? ' is-active' : ''),
            'data-type': type,
            'data-key': key,
            onClick: () => select(key)
          }, children);
        })
      );
    };

    const renderSegments = (items, type) => {
      const available = items.filter((item) => {
        const key = normalize.itemKey(item);
        return type === 'structure'
          ? resolver.supportedStructure(ctx, key)
          : resolver.supportedView(ctx, structure, key);
      });
      const select = (key) => {
        if (type === 'structure') setStructure(key);
        else if (type === 'view') setView(key);
      };
      return available.map((item, index) => {
        const key = normalize.itemKey(item);
        const label = type === 'view'
          ? normalize.viewLabelWithI18n({ ...item, label: normalize.itemLabel(item) }, index, displayValues, overviewText)
          : displayValue(normalize.itemLabel(item, normalize.titleCase(key)));
        return h('button', {
          key,
          type: 'button',
          className: 'ms-lite-segment' + (state[type] === key ? ' is-active' : ''),
          'data-type': type,
          'data-key': key,
          onClick: () => select(key)
        }, label);
      });
    };

    const viewRowHidden = !showViews || lists.views.length < 2;
    const structureRowHidden = !showStructure || lists.structures.length < 2;
    const activePayloadKey = activeBase || 'none';

    return h(React.Fragment, null,
      tabs.length ? h('div', { className: 'ms-v12-option-tabs', role: 'tablist', onKeyDown: tabNav },
        tabs.map((tab) => h('button', {
          key: tab.key,
          type: 'button',
          className: 'ms-v12-tab' + (activeTab === tab.key ? ' is-active' : ''),
          role: 'tab',
          'aria-selected': activeTab === tab.key ? 'true' : 'false',
          onClick: () => setActiveTab(tab.key)
        }, tab.label))
      ) : null,
      h('div', { className: 'ms-v12-option-panels' },
        showColor ? h('div', {
          className: 'ms-v12-option-panel' + (activeTab === 'colors' ? ' is-active' : ''),
          role: 'tabpanel',
          hidden: activeTab !== 'colors'
        }, h('p', { className: 'ms-v12-option-label' }, labels.color), renderChoiceList('colors', 'color')) : null,
        showVisor ? h('div', {
          className: 'ms-v12-option-panel' + (activeTab === 'visors' ? ' is-active' : ''),
          role: 'tabpanel',
          hidden: activeTab !== 'visors'
        }, h('p', { className: 'ms-v12-option-label' }, labels.visor), renderChoiceList('visors', 'visor')) : null
      ),
      h('div', { className: 'ms-v12-stage-wrap' },
        showViews ? h('div', { className: 'ms-v12-view-rail ms-lite-control-row', hidden: viewRowHidden },
          h('div', { className: 'ms-v12-view-title' }, labels.view),
          h('div', { className: 'ms-lite-segments' }, renderSegments(lists.views, 'view'))
        ) : null,
        h('div', { className: 'ms-v12-stage' },
          h('div', { className: 'ms-v12-stage-header' },
            h('div', null, h('span', null, labels.productView), h('strong', { 'data-lite-stage-label': '' })),
            h('span', null, root.dataset.model || '')
          ),
          h('div', { className: 'ms-lite-image-area' },
            h('div', { className: 'ms-lite-shadow', 'aria-hidden': true }),
            activePayload
              ? h('img', {
                  key: activePayloadKey,
                  className: 'ms-lite-image is-active',
                  'data-lite-image-a': '',
                  src: activeBase,
                  decoding: 'async',
                  alt: root.dataset.productTitle || '',
                  onLoad: () => setLoading(false),
                  onError: () => setLoading(false)
                })
              : null,
            activePayload && activePayload.overlay
              ? h('img', {
                  key: 'overlay-' + activePayload.overlay,
                  className: 'ms-lite-overlay' + (overlayReady ? ' is-visible' : ''),
                  'data-lite-overlay': '',
                  src: activePayload.overlay,
                  decoding: 'async',
                  style: { filter: activePayload.filter || '' },
                  onLoad: () => setOverlayReady(true),
                  onError: () => setOverlayReady(false),
                  alt: ''
                })
              : null,
            h('div', { className: 'ms-lite-loading' + (loading ? '' : ' is-hidden'), 'data-lite-loading': '' })
          ),
          h('div', { className: 'ms-v12-stage-footer' },
            h('div', { className: 'ms-v12-current' },
              h('span', null, labels.selected),
              h('strong', { 'data-lite-current': '' })
            ),
            h('button', { type: 'button', className: 'ms-lite-reset', 'data-lite-reset': '', onClick: reset }, labels.reset)
          )
        )
      ),
      showStructure ? h('div', {
        className: 'ms-v12-structure ms-lite-control-row',
        hidden: structureRowHidden
      },
        h('div', { className: 'ms-v12-structure-label' }, labels.structure),
        h('div', { className: 'ms-lite-segments' }, renderSegments(lists.structures, 'structure'))
      ) : null
    );
  }

  function mount(root) {
    const mounts = getMountRegistry();
    if (mounts.has(root)) return;
    mounts.add(root);
    const mountPoint = root.querySelector('[data-react-mount-point]') || root;
    const reactRoot = ReactDOM.createRoot(mountPoint);
    reactRoot.render(h(ErrorBoundary, { root }, h(MediaIsland, { root })));
    root.__msReactRoot = reactRoot;
  }

  function boot(scope) {
    if (!coreReady()) {
      if (!window.__msReactCoreWait) {
        window.__msReactCoreWait = setInterval(() => {
          if (coreReady()) {
            clearInterval(window.__msReactCoreWait);
            window.__msReactCoreWait = null;
            document.querySelectorAll('[data-react-media-root]').forEach(mount);
          }
        }, 100);
        setTimeout(() => {
          if (window.__msReactCoreWait) {
            clearInterval(window.__msReactCoreWait);
            window.__msReactCoreWait = null;
            if (!coreReady()) {
              console.error('[MASONTEX Media React] MasontexMediaCore did not load within 5s');
              document.querySelectorAll('[data-react-media-root]').forEach((r) => {
                r.dataset.mediaUnavailable = 'true';
              });
            }
          }
        }, 5000);
      }
      return;
    }
    (scope || document).querySelectorAll('[data-react-media-root]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
